import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from './config';
import logger from '@/utils/logger';

const API = API_URL;

// Helper: extrae detail accionable del backend Spotify si está disponible
const _spotifyErrMsg = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' && detail.length > 0 ? detail : fallback;
};

const SpotifyContext = createContext();

export const useSpotify = () => {
  const context = useContext(SpotifyContext);
  if (!context) {
    throw new Error('useSpotify must be used within SpotifyProvider');
  }
  return context;
};

export const SpotifyProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(localStorage.getItem('spotify_access_token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('spotify_refresh_token'));
  const [expiresAt, setExpiresAt] = useState(parseInt(localStorage.getItem('spotify_expires_at') || '0'));
  const [userProfile, setUserProfile] = useState(null);
  const [playbackState, setPlaybackState] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasCheckedBackend, setHasCheckedBackend] = useState(false);
  // Enhancement 2026-04-20: queue count + up-next para el badge del header
  const [queueCount, setQueueCount] = useState(0);
  const [upNext, setUpNext] = useState([]);

  // Al montar, verificar si había una auth pendiente y resetear estado
  useEffect(() => {
    const authPending = sessionStorage.getItem('spotify_auth_pending');
    if (authPending) {
      logger.log('[Spotify] Auth was pending, clearing and checking backend...');
      sessionStorage.removeItem('spotify_auth_pending');
      sessionStorage.removeItem('spotify_auth_time');
      setIsLoading(false);
    }
  }, []);

  // Check if token is expired
  const isTokenExpired = () => {
    return Date.now() / 1000 > expiresAt;
  };

  // Recuperar tokens del backend (para PWAs donde localStorage no se comparte)
  const fetchStoredTokens = useCallback(async () => {
    // Obtener token de autenticación de la app
    const authToken = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (!authToken) {
      logger.log('[Spotify] No auth token, skipping backend check');
      return false;
    }
    
    try {
      logger.log('[Spotify] Checking backend for stored tokens...');
      const response = await axios.get(`${API}/auth/spotify/stored-tokens`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (response.data.connected) {
        logger.log('[Spotify] Found stored tokens in backend!');
        const { access_token, refresh_token, expires_at } = response.data;
        
        // Guardar en localStorage y estado
        setAccessToken(access_token);
        setRefreshToken(refresh_token);
        setExpiresAt(expires_at);
        setIsLoading(false);
        
        localStorage.setItem('spotify_access_token', access_token);
        localStorage.setItem('spotify_refresh_token', refresh_token);
        localStorage.setItem('spotify_expires_at', expires_at.toString());
        localStorage.removeItem('spotify_auth_pending'); // Limpiar flag
        
        return true;
      }
      setIsLoading(false);
      return false;
    } catch (error) {
      logger.log('[Spotify] No stored tokens found:', error.message);
      setIsLoading(false);
      return false;
    }
  }, []);

  // Refresh access token
  const refreshAccessToken = async () => {
    if (!refreshToken) return false;

    try {
      const response = await axios.post(`${API}/spotify/refresh-token`, null, {
        params: { refresh_token: refreshToken }
      });

      const { access_token, expires_at } = response.data;
      
      setAccessToken(access_token);
      setExpiresAt(expires_at);
      
      localStorage.setItem('spotify_access_token', access_token);
      localStorage.setItem('spotify_expires_at', expires_at.toString());
      
      return true;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    }
  };

  // Get user profile
  const fetchUserProfile = async () => {
    if (!accessToken) return;

    // Check if token is expired
    if (isTokenExpired()) {
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        disconnectSpotify();
        return;
      }
    }

    try {
      const response = await axios.get(`${API}/spotify/user-profile`, {
        params: { access_token: accessToken }
      });
      
      setUserProfile(response.data);
      setIsConnected(true);
      
      if (!response.data.is_premium) {
        toast.warning('Spotify Premium requerido para control de reproducción');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setIsConnected(false);
    }
  };

  // Get playback state
  const fetchPlaybackState = async () => {
    if (!accessToken || !isConnected) return;

    try {
      const response = await axios.get(`${API}/spotify/playback-state`, {
        params: { access_token: accessToken }
      });
      setPlaybackState(response.data);
    } catch (error) {
      console.error('Error fetching playback state:', error);
    }
  };

  // Enhancement 2026-04-20: fetchQueueCount para el badge del header
  const fetchQueueCount = async () => {
    if (!accessToken || !isConnected) {
      setQueueCount(0);
      setUpNext([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/spotify/queue`, {
        params: { access_token: accessToken }
      });
      const items = response.data?.queue || [];
      setQueueCount(items.length);
      setUpNext(items.slice(0, 5).map(t => ({
        id: t.id,
        title: t.name,
        artist: t.artists?.map(a => a.name).join(', ') || ''
      })));
    } catch (error) {
      // Silencioso: 404 si no hay device activo es normal (el badge simplemente muestra 0)
      setQueueCount(0);
      setUpNext([]);
    }
  };

  // Poll queue cada 15s solo cuando hay conexión — barato, informativo
  useEffect(() => {
    if (!isConnected || !accessToken) return;
    fetchQueueCount();
    const interval = setInterval(fetchQueueCount, 15000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: fetchQueueCount
  }, [isConnected, accessToken]);

  // Login to Spotify - PWA compatible
  const loginSpotify = async () => {
    setIsLoading(true);
    try {
      const authToken = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/spotify/auth-url`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      const authUrl = response.data.auth_url;
      
      // Detectar si es PWA standalone
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                          window.navigator.standalone === true ||
                          document.referrer.includes('android-app://');
      
      logger.log('[Spotify Auth] isStandalone:', isStandalone);
      
      if (isStandalone) {
        // En PWA: abrir en ventana externa del navegador del sistema
        // Esto evita el in-app browser que causa problemas con los tokens
        toast.info('📱 Se abrirá el navegador para autenticar. Después vuelve aquí y pulsa "Verificar".', { duration: 5000 });
        
        // Guardar estado de auth pendiente
        localStorage.setItem('spotify_auth_pending', 'true');
        localStorage.setItem('spotify_auth_time', Date.now().toString());
        
        // Abrir en ventana externa - en iOS esto abre Safari
        // El _blank con noopener evita el in-app browser en muchos casos
        const authWindow = window.open(authUrl, '_blank', 'noopener,noreferrer');
        
        // Si window.open falla (bloqueado), usar location.href como fallback
        if (!authWindow) {
          window.location.href = authUrl;
        }
        
        setIsLoading(false);
      } else {
        // En navegador normal: redirección directa
        window.location.href = authUrl;
      }
      
    } catch (error) {
      console.error('Error initiating Spotify login:', error);
      toast.error('Error al conectar con Spotify');
      setIsLoading(false);
    }
  };

  // Disconnect Spotify
  const disconnectSpotify = () => {
    setAccessToken(null);
    setRefreshToken(null);
    setExpiresAt(0);
    setUserProfile(null);
    setPlaybackState(null);
    setIsConnected(false);
    
    localStorage.removeItem('spotify_access_token');
    localStorage.removeItem('spotify_refresh_token');
    localStorage.removeItem('spotify_expires_at');

    // Borrar también el token guardado en la cuenta de este DJ (si no, se
    // re-conectaría solo al recargar vía /stored-tokens).
    const authToken = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (authToken) {
      axios.delete(`${API}/auth/spotify/disconnect`, {
        headers: { Authorization: `Bearer ${authToken}` },
      }).catch(() => { /* silencioso */ });
    }
    
    toast.info('Desconectado de Spotify');
  };

  // Add single track to queue
  const addToQueue = async (trackUri) => {
    if (!accessToken) {
      toast.error('Por favor conecta con Spotify primero');
      return false;
    }

    try {
      await axios.post(`${API}/spotify/queue/add`, {
        track_uri: trackUri,
        access_token: accessToken
      });
      
      toast.success('Canción añadida a la cola de Spotify');
      return true;
    } catch (error) {
      console.error('Error adding to queue:', error);
      toast.error(_spotifyErrMsg(error, 'Error al añadir a la cola'));
      return false;
    }
  };

  // Add multiple tracks to queue
  const addMultipleToQueue = async (trackUris) => {
    if (!accessToken) {
      toast.error('Por favor conecta con Spotify primero');
      return false;
    }

    try {
      const response = await axios.post(`${API}/spotify/queue/add-multiple`, {
        track_uris: trackUris,
        access_token: accessToken
      });
      
      const { added_count, total, failed_tracks } = response.data;
      
      if (failed_tracks.length > 0) {
        toast.warning(`${added_count}/${total} canciones añadidas (${failed_tracks.length} fallaron)`);
      } else {
        toast.success(`${added_count} canciones añadidas a Spotify`);
      }
      
      return true;
    } catch (error) {
      console.error('Error adding multiple to queue:', error);
      toast.error(_spotifyErrMsg(error, 'Error al añadir canciones a la cola'));
      return false;
    }
  };

  // Toggle shuffle
  const toggleShuffle = async (state) => {
    if (!accessToken) return false;

    try {
      await axios.put(`${API}/spotify/playback/shuffle`, {
        state,
        access_token: accessToken
      });
      
      toast.success(`Shuffle ${state ? 'activado' : 'desactivado'}`);
      fetchPlaybackState();
      return true;
    } catch (error) {
      console.error('Error toggling shuffle:', error);
      toast.error(_spotifyErrMsg(error, 'Error al cambiar shuffle'));
      return false;
    }
  };

  // Set repeat mode
  const setRepeatMode = async (state) => {
    if (!accessToken) return false;

    try {
      await axios.put(`${API}/spotify/playback/repeat`, {
        state,
        access_token: accessToken
      });
      
      const modeNames = {
        'off': 'desactivado',
        'track': 'canción actual',
        'context': 'lista completa'
      };
      
      toast.success(`Repeat: ${modeNames[state]}`);
      fetchPlaybackState();
      return true;
    } catch (error) {
      console.error('Error setting repeat:', error);
      toast.error(_spotifyErrMsg(error, 'Error al cambiar repeat'));
      return false;
    }
  };

  // Set crossfade
  const setCrossfade = async (durationMs) => {
    if (!accessToken) return false;

    try {
      await axios.put(`${API}/spotify/playback/crossfade`, {
        duration_ms: durationMs,
        access_token: accessToken
      });
      
      const seconds = durationMs / 1000;
      toast.success(`Crossfade: ${seconds}s`);
      return true;
    } catch (error) {
      console.error('Error setting crossfade:', error);
      toast.error(_spotifyErrMsg(error, 'Error al configurar crossfade'));
      return false;
    }
  };

  // Pause playback
  const pausePlayback = async () => {
    if (!accessToken) return false;

    try {
      await axios.post(`${API}/spotify/playback/pause`, {
        access_token: accessToken
      });
      
      fetchPlaybackState();
      return true;
    } catch (error) {
      console.error('Error pausing:', error);
      toast.error(_spotifyErrMsg(error, 'Error al pausar'));
      return false;
    }
  };

  // Resume playback
  const resumePlayback = async () => {
    if (!accessToken) return false;

    try {
      await axios.post(`${API}/spotify/playback/resume`, {
        access_token: accessToken
      });
      
      fetchPlaybackState();
      return true;
    } catch (error) {
      console.error('Error resuming:', error);
      toast.error(_spotifyErrMsg(error, 'Error al reanudar'));
      return false;
    }
  };

  // Next track
  const nextTrack = async () => {
    if (!accessToken) return false;

    try {
      await axios.post(`${API}/spotify/playback/next`, {
        access_token: accessToken
      });
      
      fetchPlaybackState();
      return true;
    } catch (error) {
      console.error('Error skipping:', error);
      toast.error(_spotifyErrMsg(error, 'Error al saltar canción'));
      return false;
    }
  };

  // Previous track
  const previousTrack = async () => {
    if (!accessToken) return false;

    try {
      await axios.post(`${API}/spotify/playback/previous`, {
        access_token: accessToken
      });
      
      fetchPlaybackState();
      return true;
    } catch (error) {
      console.error('Error going back:', error);
      toast.error(_spotifyErrMsg(error, 'Error al retroceder'));
      return false;
    }
  };

  // Auto-refresh token before expiry
  useEffect(() => {
    if (!accessToken || !refreshToken) return;

    const checkAndRefreshToken = async () => {
      // Refresh 5 minutes before expiry
      const timeUntilExpiry = expiresAt - (Date.now() / 1000);
      
      if (timeUntilExpiry < 300) { // Less than 5 minutes
        logger.log('🔄 Token expiring soon, refreshing...');
        const success = await refreshAccessToken();
        if (!success) {
          toast.warning('Sesión de Spotify expirada. Reconecta por favor.');
          disconnectSpotify();
        }
      }
    };

    // Check immediately
    checkAndRefreshToken();

    // Check every minute
    const interval = setInterval(checkAndRefreshToken, 60000);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: refreshAccessToken
  }, [accessToken, refreshToken, expiresAt]);

  // PWA: Si no hay tokens locales, intentar recuperarlos del backend
  // Se ejecuta al montar y cuando la app vuelve al primer plano
  useEffect(() => {
    const checkBackendTokens = async (retryCount = 0) => {
      // Si ya tenemos token local y estamos conectados, no hace falta
      if (accessToken && isConnected) {
        logger.log('[Spotify] Already connected, skipping backend check');
        return;
      }
      
      // Verificar que hay auth_token
      const authToken = localStorage.getItem('auth_token');
      if (!authToken) {
        logger.log('[Spotify] No auth_token, retry:', retryCount);
        // Reintentar hasta 5 veces con delay creciente
        if (retryCount < 5) {
          setTimeout(() => checkBackendTokens(retryCount + 1), 1000 * (retryCount + 1));
        }
        return;
      }
      
      logger.log('[Spotify] Checking backend for stored tokens...');
      const found = await fetchStoredTokens();
      if (found) {
        logger.log('[Spotify] ✅ Found tokens in backend!');
        toast.success('✅ Spotify conectado');
      } else {
        logger.log('[Spotify] No tokens found in backend');
      }
    };
    
    // Verificar al montar (con delay para dar tiempo a cargar auth)
    if (!hasCheckedBackend) {
      setHasCheckedBackend(true);
      setTimeout(() => checkBackendTokens(0), 1000);
    }
    
    // Escuchar cuando la app vuelve al primer plano (para PWA)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !accessToken) {
        logger.log('[Spotify] App visible, checking for tokens...');
        checkBackendTokens(0);
      }
    };
    
    // Escuchar cuando la ventana recibe foco
    const handleFocus = () => {
      if (!accessToken) {
        logger.log('[Spotify] Window focused, checking for tokens...');
        checkBackendTokens(0);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [hasCheckedBackend, accessToken, isConnected, fetchStoredTokens]);

  // Load user profile on mount if token exists
  useEffect(() => {
    if (accessToken) {
      fetchUserProfile();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: fetchUserProfile
  }, [accessToken]);

  // Poll playback state every 5 seconds
  useEffect(() => {
    if (isConnected) {
      const interval = setInterval(fetchPlaybackState, 5000);
      return () => clearInterval(interval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: fetchPlaybackState
  }, [isConnected]);

  // Listen for localStorage changes (for PWA popup auth flow)
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Detectar cuando el popup guarda los tokens
      if (e.key === 'spotify_access_token' || e.key === 'spotify_auth_timestamp') {
        const newAccessToken = localStorage.getItem('spotify_access_token');
        const newRefreshToken = localStorage.getItem('spotify_refresh_token');
        const newExpiresAt = localStorage.getItem('spotify_expires_at');
        
        if (newAccessToken && newAccessToken !== accessToken) {
          logger.log('🎵 Detected Spotify auth from popup');
          setAccessToken(newAccessToken);
          setRefreshToken(newRefreshToken);
          setExpiresAt(parseInt(newExpiresAt || '0'));
          setIsLoading(false);
          
          // Fetch profile will trigger and set isConnected
          toast.success('✅ ¡Conectado con Spotify!');
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // También polling para detectar cambios en la misma ventana (PWA)
    let pollInterval;
    if (isLoading) {
      pollInterval = setInterval(() => {
        const storedToken = localStorage.getItem('spotify_access_token');
        const storedTimestamp = localStorage.getItem('spotify_auth_timestamp');
        const lastCheckedTimestamp = sessionStorage.getItem('spotify_last_check');
        
        if (storedToken && storedTimestamp !== lastCheckedTimestamp) {
          sessionStorage.setItem('spotify_last_check', storedTimestamp);
          
          if (storedToken !== accessToken) {
            logger.log('🎵 Detected Spotify auth via polling');
            setAccessToken(storedToken);
            setRefreshToken(localStorage.getItem('spotify_refresh_token'));
            setExpiresAt(parseInt(localStorage.getItem('spotify_expires_at') || '0'));
            setIsLoading(false);
            toast.success('✅ ¡Conectado con Spotify!');
          }
        }
      }, 1000);
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [accessToken, isLoading]);

  // Función pública para verificar tokens manualmente (para el botón "Ya autentiqué")
  const checkStoredTokens = async () => {
    logger.log('[Spotify] Manual check for stored tokens...');
    const found = await fetchStoredTokens();
    if (found) {
      toast.success('✅ ¡Spotify conectado!');
      return true;
    } else {
      toast.error('No se encontró conexión. Intenta autenticar de nuevo.');
      return false;
    }
  };

  const value = {
    accessToken,
    userProfile,
    playbackState,
    isConnected,
    isLoading,
    isPremium: userProfile?.is_premium || false,
    loginSpotify,
    disconnectSpotify,
    addToQueue,
    addMultipleToQueue,
    toggleShuffle,
    setRepeatMode,
    setCrossfade,
    pausePlayback,
    resumePlayback,
    nextTrack,
    previousTrack,
    refreshPlaybackState: fetchPlaybackState,
    // Enhancement 2026-04-20: queue count para el badge del header
    queueCount,
    upNext,
    refreshQueueCount: fetchQueueCount,
    checkStoredTokens // Nueva función expuesta
  };

  return (
    <SpotifyContext.Provider value={value}>
      {children}
    </SpotifyContext.Provider>
  );
};
